import { inject, Injectable } from '@angular/core'

import { forkJoin, map, Observable, of, switchMap, tap } from 'rxjs'
import { EntriesService } from 'superlikers-microsites-api'
import {
  CircuitKpisSummary,
  ExternalCircuitKpisSummary,
  ExternalKpi,
  ExternalKpiDetails,
  ExternalKpiSummary,
  Kpi,
  KpisSummary
} from '../interfaces/kpis.interface'
import { adapterCircuitKpiSummary, adapterKpi, adapterKpiCircuit, adapterKpiSummary } from '../adapters/kpis.adapter'
import { CacheService } from '@shared/services/cache.service'

@Injectable({
  providedIn: 'root'
})
export class KpisService {
  private entriesService = inject(EntriesService)
  private cacheService = inject(CacheService)

  private KPI_CACHE_KEY = '_kpis'
  private KPI_SUMMARY_CACHE_KEY = '_kpis_summary'
  private CIRCUITS_KPIS_CACHE_KEY = '_circuits_kpis'
  private CIRCUITS_KPIS_SUMMARY_CACHE_KEY = '_circuits_kpis_summary'

  /**
   * Obtiene el resumen de los KPIs para un usuario y categoría específicos
   * Primero intenta recuperar el resumen de los KPIs de la caché y, si no existen, los solicita a la API
   *
   * @param {string} month - Categoría de los KPIs a obtener (ej: junio_2025)
   * @param {string} uid - Identificador único del usuario
   * @param {boolean} self - Indica si se están obteniendo los KPIs del usuario logueado (true) o de otro usuario (false)
   * @returns {Observable<KpisSummary>} Observable con el resumen de los KPIs
   */
  fetchKpisSummary(month: string, uid: string, self: boolean): Observable<KpisSummary> {
    return this.cacheService.getFromCache<KpisSummary[]>(this.KPI_SUMMARY_CACHE_KEY).pipe(
      switchMap(cached => {
        const userKpisSummary = cached?.find(item => item.userUid === uid && item.month === month)
        if (userKpisSummary) {
          if (userKpisSummary.isFetched && userKpisSummary.percentage === undefined) {
            return of({} as KpisSummary)
          }
          return of(userKpisSummary)
        }

        return this.fetchKpisSummaryFromApi(month, uid, self)
      })
    )
  }

  private fetchKpisSummaryFromApi(month: string, uid: string, self: boolean): Observable<KpisSummary> {
    const params: Record<string, unknown> = { _type: 'External', category: `summary-${month}` }
    if (!self) params['participant_uid'] = uid

    return this.entriesService.getEntries<ExternalKpiSummary>(params).pipe(
      map(response => {
        const external = response.data.at(0)
        if (!external) {
          return { userUid: uid, month, isFetched: true } as KpisSummary
        }

        const summary = adapterKpiSummary(uid, external)
        return { ...summary, isFetched: true }
      }),
      tap(summary => {
        this.cacheService.getFromCache<KpisSummary[]>(this.KPI_SUMMARY_CACHE_KEY).subscribe(cached => {
          this.cacheService.saveToCache(this.KPI_SUMMARY_CACHE_KEY, [...(cached ?? []), summary])
        })
      })
    )
  }

  /**
   * Obtiene los KPIs para un usuario y categoría específicos
   * Primero intenta recuperarlos de la caché y, si no existen, los solicita a la API
   *
   * @param {string} month - Mes de los KPIs a obtener (ej: junio_2025)
   * @param {string} uid - Identificador único del usuario
   * @param {boolean} self - Indica si se están obteniendo los KPIs del usuario logueado (true) o de otro usuario (false)
   * @returns {Observable<Kpi[]>} Observable con el array de KPIs
   */
  fetchKpis(month: string, uid: string, self: boolean): Observable<Kpi[]> {
    const category = `kpi_${month}`

    return this.getKpisFromCache(uid, category, this.KPI_CACHE_KEY).pipe(
      switchMap(cachedKpis => {
        if (cachedKpis === null) {
          return of([]) // Marker found, data is intentionally empty
        }
        if (cachedKpis.length > 0) {
          return of(cachedKpis) // Cache hit with data
        }
        // Cache miss, fetch from API
        return this.fetchKpisFromApi(month, uid, self, category)
      })
    )
  }

  private fetchKpisFromApi(month: string, uid: string, self: boolean, category: string): Observable<Kpi[]> {
    const params: Record<string, unknown> = { _type: 'External', category }
    if (!self) params['participant_uid'] = uid

    return this.entriesService.getEntries<ExternalKpi>(params).pipe(
      map(response => {
        if (response.data.length === 0) {
          return [
            {
              userUid: uid,
              category,
              isFetched: true,
              slug: '',
              month: '',
              name: '',
              goal: 0,
              progress: 0,
              percentage: 0,
              show: false,
              details: []
            }
          ] as Kpi[]
        }
        return response.data.map(external => adapterKpi(uid, external))
      }),
      tap(kpis => this.saveKpisToCache(kpis, this.KPI_CACHE_KEY))
    )
  }

  /**
   * Obtiene el resumen de los KPIs para un circuito específico y mes específico
   * Primero intenta recuperar el resumen de los KPIs de la caché y, si no existen, los solicita a la API
   *
   * @param {string} circuit - slug del circuito
   * @param {string} uid - Identificador único del usuario
   * @param {string} month - Mes de los KPIs a obtener (ej: junio_2025)
   * @param {boolean} self - Indica si se están obteniendo los KPIs del usuario logueado (true) o de otro usuario (false)
   * @returns {Observable<CircuitKpisSummary | null>} Observable con el resumen de los KPIs del circuito
   */
  fetchCircuitKpisSummary(
    circuit: string,
    uid: string,
    month: string,
    self: boolean
  ): Observable<CircuitKpisSummary | null> {
    const category = `summary-${circuit}-${month}`

    return this.cacheService.getFromCache<CircuitKpisSummary[]>(this.CIRCUITS_KPIS_SUMMARY_CACHE_KEY).pipe(
      switchMap(cached => {
        const userCircuitKpisSummary = cached?.find(
          item => item.userUid === uid && item.circuit === circuit && item.month === month
        )
        if (userCircuitKpisSummary) {
          if (userCircuitKpisSummary.isFetched && userCircuitKpisSummary.percentage === undefined) {
            return of(null)
          }
          return of(userCircuitKpisSummary)
        }

        return this.fetchCircuitKpisSummaryFromApi(circuit, uid, month, self, category)
      })
    )
  }

  private fetchCircuitKpisSummaryFromApi(
    circuit: string,
    uid: string,
    month: string,
    self: boolean,
    category: string
  ): Observable<CircuitKpisSummary | null> {
    const params: Record<string, unknown> = { _type: 'External', category }
    if (!self) params['participant_uid'] = uid

    return this.entriesService.getEntries<ExternalCircuitKpisSummary>(params).pipe(
      map(response => {
        const external = response.data.at(0)
        if (!external) {
          return { userUid: uid, month, circuit, isFetched: true } as CircuitKpisSummary
        }

        const summary = adapterCircuitKpiSummary(uid, external)
        return { ...summary, isFetched: true }
      }),
      tap(summary => {
        if (!summary) return

        this.cacheService.getFromCache<CircuitKpisSummary[]>(this.CIRCUITS_KPIS_SUMMARY_CACHE_KEY).subscribe(cached => {
          this.cacheService.saveToCache(this.CIRCUITS_KPIS_SUMMARY_CACHE_KEY, [...(cached ?? []), summary])
        })
      })
    )
  }

  /**
   * Obtiene los KPIs asociados a un circuito específico
   *
   * @param {string} circuit - slug del circuito
   * @param {string} uid - Identificador único del usuario
   * @param {string} month - Mes de los kpis que se quieren obtener (ej: junio_2025)
   * @param {boolean} self - Indica si se están obteniendo los KPIs del usuario logueado (true) o de otro usuario (false)
   * @returns {Observable<Kpi[]>} Observable con los KPIs del circuito
   */
  fetchCircuitKpis(circuit: string, uid: string, month: string, self: boolean): Observable<Kpi[]> {
    const category = `${circuit}-${month}`

    return this.getKpisFromCache(uid, category, this.CIRCUITS_KPIS_CACHE_KEY).pipe(
      switchMap(cachedKpis => {
        if (cachedKpis === null) {
          return of([]) // Marker found, data is intentionally empty
        }
        if (cachedKpis.length > 0) {
          return of(cachedKpis) // Cache hit with data
        }
        // Cache miss, fetch from API
        return this.fetchCircuitKpisFromApi(circuit, uid, month, self, category)
      })
    )
  }

  private fetchCircuitKpisFromApi(
    circuit: string,
    uid: string,
    month: string,
    self: boolean,
    category: string
  ): Observable<Kpi[]> {
    // fetch info about kpis
    const kpisParams: Record<string, unknown> = { _type: 'External', category }
    if (!self) kpisParams['participant_uid'] = uid
    const kpis$ = this.entriesService.getEntries<ExternalKpi>(kpisParams).pipe(map(response => response.data))

    // fetch info about details
    const detailsParams: Record<string, unknown> = { _type: 'External', category: `${category}-details` }
    if (!self) detailsParams['participant_uid'] = uid
    const kpisDetails$ = this.entriesService
      .getEntries<ExternalKpiDetails>(detailsParams)
      .pipe(map(response => response.data))

    return forkJoin([kpis$, kpisDetails$]).pipe(
      map(([kpisExternals, detailsExternals]) => {
        if (kpisExternals.length === 0) {
          return [
            {
              userUid: uid,
              category,
              isFetched: true,
              slug: '',
              month: '',
              name: '',
              goal: 0,
              progress: 0,
              percentage: 0,
              show: false,
              details: []
            }
          ] as Kpi[]
        }
        return kpisExternals.map(external => {
          const details = detailsExternals.filter(detail => detail.data?.kpi === external.slug)
          return adapterKpiCircuit(uid, external, details)
        })
      }),
      tap(kpis => this.saveKpisToCache(kpis, this.CIRCUITS_KPIS_CACHE_KEY))
    )
  }

  /**
   * Obtiene los KPIs desde la caché, si el cacheKey es KPI_CACHE_KEY, filtra por uid y categoría,
   * si el cacheKey es CIRCUITS_KPIS_CACHE_KEY, filtra por uid y categoría, y si el KPI tiene una fecha,
   * también filtra por fecha.
   *
   * @param {string} uid - Identificador único del usuario
   * @param {string} category - Categoría de los KPIs a filtrar
   * @param {string} cacheKey - Clave de caché a utilizar
   * @returns {Kpi[]} Array de KPIs filtrados por usuario y categoría
   * @private
   */
  private getKpisFromCache(uid: string, category: string, cacheKey: string): Observable<Kpi[] | null> {
    return this.cacheService.getFromCache<Kpi[]>(cacheKey).pipe(
      map(cached => {
        if (!cached) return [] // Nothing in cache, treat as miss

        const userKpis = cached.filter(kpi => kpi.userUid === uid && kpi.category === category)

        if (userKpis.length === 0) return [] // No specific data for this user, treat as miss

        // Marker found, means data was fetched and it was empty
        if (userKpis.length === 1 && userKpis[0].isFetched) {
          return null
        }

        return userKpis
      })
    )
  }

  /**
   * Guarda los KPIs en la caché, añadiéndolos a los ya existentes
   *
   * @param {Kpi[]} kpis - Array de KPIs a guardar en caché
   * @param {string} cacheKey - Clave de caché a utilizar
   * @private
   */
  private saveKpisToCache(kpis: Kpi[], cacheKey: string) {
    this.cacheService.getFromCache<Kpi[]>(cacheKey).subscribe(cached => {
      this.cacheService.saveToCache(cacheKey, [...(cached ?? []), ...kpis])
    })
  }
}
